// Admin settings panel (PLAN.md Task 5.4, D-18 / D-22).

import {
  AI_RATES_CHECKED_ON,
  dailyTokenCapUsd,
  DEFAULT_AI_CHAT_DAILY_TOKEN_LIMIT,
  DEFAULT_AI_CHAT_MODEL_ID,
  DEFAULT_AI_DAILY_LIMIT,
  DEFAULT_BEDROCK_MODEL_ID,
  isAllowedAiModelId,
  type AiChatTranscriptMode,
} from "@checklist/shared";
import { useEffect, useState, type FormEvent } from "react";
import { ChevronDown, ListPlus, MessageSquare } from "lucide-react";
import { Navigate } from "react-router-dom";
import {
  ErrorBanner,
  fieldCls,
  labelCls,
  SuccessBanner,
} from "../components/AuthShell.js";
import { Button } from "../components/ui/Button.js";
import { PageHeader } from "../components/ui/PageHeader.js";
import { AdminNav } from "../components/admin/AdminNav.js";
import { useAuth } from "../context/AuthContext.js";
import {
  ApiError,
  fetchAdminSettings,
  updateAdminSettings,
} from "../lib/api.js";
import { aiModelGroups, type AiModelSurface } from "../lib/aiModelOptions.js";

/** Shown only on the Bedrock Sonnet 5 option: the account may still be gated by AWS. */
const BEDROCK_SONNET_5_WARNING =
  "AWS may still gate this account -- select it, then generate or chat to test.";

interface ModelPickerProps {
  name: string;
  label: string;
  surface: AiModelSurface;
  value: string;
  onChange: (id: string) => void;
  keyConfigured: boolean;
}

/** Compact selection with an optional full comparison, priced from the shared catalog. */
function ModelPicker({ name, label, surface, value, onChange, keyConfigured }: ModelPickerProps) {
  const groups = aiModelGroups(surface);
  const selectedGroup = groups.find((group) => group.options.some((opt) => opt.id === value));
  const selected = selectedGroup?.options.find((opt) => opt.id === value);

  return (
    <div>
      <label htmlFor={name} className={labelCls}>{label}</label>
      <select
        id={name}
        name={name}
        className={fieldCls}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={`${name}-pricing ${name}-availability`}
      >
        {groups.map((group) => (
          <optgroup key={group.provider} label={group.heading}>
            {group.options.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
          </optgroup>
        ))}
      </select>
      <div id={`${name}-pricing`} className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-muted-sm" aria-live="polite">
        <p className="font-medium text-ink">{selectedGroup?.heading}</p>
        <p className="mt-0.5">{selected?.note}</p>
      </div>
      <div id={`${name}-availability`} className="mt-2 text-xs" aria-live="polite">
        {selectedGroup?.provider === "anthropic" && (keyConfigured ? (
          <p className="text-success">Anthropic API key configured</p>
        ) : (
          <p className="text-warning">Key not set - run scripts/set-anthropic-api-key.ps1 before saving.</p>
        ))}
        {value === "anthropic.claude-sonnet-5" && (
          <p className="text-warning">{BEDROCK_SONNET_5_WARNING}</p>
        )}
      </div>
      <details className="group mt-3">
        <summary className="flex cursor-pointer list-none items-center gap-1 text-xs font-medium text-brand-700 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 [&::-webkit-details-marker]:hidden">
          <ChevronDown aria-hidden="true" className="size-4 transition-transform group-open:rotate-180" />
          Compare model pricing
        </summary>
        <fieldset className="mt-3 space-y-3">
          <legend className="sr-only">Compare models for {label.toLowerCase()}</legend>
          {groups.map((group) => (
            <div key={group.provider}>
              <h3 className="text-xs font-semibold text-muted-sm">{group.heading}</h3>
              {group.provider === "anthropic" && (
                <p className={`mt-1 text-xs ${keyConfigured ? "text-success" : "text-warning"}`}>
                  {keyConfigured ? "Key configured" : "Key not set - run scripts/set-anthropic-api-key.ps1"}
                </p>
              )}
              <div className="mt-2 space-y-1">
                {group.options.map((opt) => (
                  <label key={opt.id} className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 hover:bg-brand-50/40 ${value === opt.id ? "border-brand-500 bg-brand-50/60" : "border-line"}`}>
                    <input type="radio" name={`${name}-comparison`} className="mt-1 accent-brand-600" value={opt.id} checked={value === opt.id} onChange={() => onChange(opt.id)} />
                    <span>
                      <span className="block text-sm font-medium text-ink">{opt.label}</span>
                      <span className="block text-xs text-muted-sm">{opt.note}</span>
                      {opt.id === "anthropic.claude-sonnet-5" && (
                        <span className="block text-xs text-muted-sm">{BEDROCK_SONNET_5_WARNING}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </fieldset>
      </details>
    </div>
  );
}

// Chat is metered in tokens, not generations (D-A12), so this range is unrelated
// to the per-generation daily limit above. Mirrors api/src/lib/admin.ts.
const CHAT_TOKEN_LIMIT_MIN = 10_000;
const CHAT_TOKEN_LIMIT_MAX = 5_000_000;

export function AdminPage() {
  const { user } = useAuth();
  const isAdmin = Boolean(user?.groups.includes("admin"));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [bedrockModelId, setBedrockModelId] = useState<string>(DEFAULT_BEDROCK_MODEL_ID);
  const [aiDailyLimit, setAiDailyLimit] = useState(DEFAULT_AI_DAILY_LIMIT);
  const [aiChatModelId, setAiChatModelId] = useState<string>(DEFAULT_AI_CHAT_MODEL_ID);
  const [aiChatDailyTokenLimit, setAiChatDailyTokenLimit] = useState(
    DEFAULT_AI_CHAT_DAILY_TOKEN_LIMIT
  );
  const [aiChatTranscriptMode, setAiChatTranscriptMode] = useState<AiChatTranscriptMode>("full");
  const [anthropicKeyConfigured, setAnthropicKeyConfigured] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const settings = await fetchAdminSettings();
        if (cancelled) return;
        setBedrockModelId(settings.bedrockModelId);
        setAiDailyLimit(settings.aiDailyLimit);
        setAiChatModelId(settings.aiChatModelId);
        setAiChatDailyTokenLimit(settings.aiChatDailyTokenLimit);
        setAiChatTranscriptMode(settings.aiChatTranscriptMode);
        setAnthropicKeyConfigured(settings.anthropicKeyConfigured);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        if (err instanceof ApiError && err.status === 403) {
          setError("FORBIDDEN");
          return;
        }
        setError(err instanceof ApiError ? err.message : "Failed to load settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!isAllowedAiModelId(bedrockModelId)) {
      setError("Select a valid model.");
      return;
    }
    if (!Number.isInteger(aiDailyLimit) || aiDailyLimit < 1 || aiDailyLimit > 100) {
      setError("Daily limit must be an integer from 1 to 100.");
      return;
    }
    if (!isAllowedAiModelId(aiChatModelId)) {
      setError("Select a valid chat model.");
      return;
    }
    if (
      !Number.isInteger(aiChatDailyTokenLimit) ||
      aiChatDailyTokenLimit < CHAT_TOKEN_LIMIT_MIN ||
      aiChatDailyTokenLimit > CHAT_TOKEN_LIMIT_MAX
    ) {
      setError(
        `Chat token limit must be an integer from ${CHAT_TOKEN_LIMIT_MIN.toLocaleString()} to ${CHAT_TOKEN_LIMIT_MAX.toLocaleString()}.`
      );
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await updateAdminSettings({
        bedrockModelId,
        aiDailyLimit,
        aiChatModelId,
        aiChatDailyTokenLimit,
        aiChatTranscriptMode,
      });
      setBedrockModelId(saved.bedrockModelId);
      setAiDailyLimit(saved.aiDailyLimit);
      setAiChatModelId(saved.aiChatModelId);
      setAiChatDailyTokenLimit(saved.aiChatDailyTokenLimit);
      setAiChatTranscriptMode(saved.aiChatTranscriptMode);
      setSuccess("Settings saved.");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      if (err instanceof ApiError && err.code === "ANTHROPIC_KEY_NOT_SET") {
        setError("Set the Anthropic API key before selecting an Anthropic API model.");
        return;
      }
      setError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:py-10">
      <PageHeader
        title="Admin · AI settings"
        subtitle="Choose a model and daily budget for each workflow."
      />
      <AdminNav />

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      {loading ? (
        <p className="mt-8 text-sm text-muted">Loading settings…</p>
      ) : (
        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => void onSave(e)}
        >
          <div className="grid items-start gap-4 md:grid-cols-2">
            <section aria-labelledby="generation-heading" className="min-w-0 rounded-2xl border border-line bg-surface p-5 shadow-sm">
              <div className="mb-5 flex items-start gap-3">
                <span className="rounded-lg bg-brand-50 p-2 text-brand-700"><ListPlus aria-hidden="true" className="size-5" /></span>
                <div>
                  <h2 id="generation-heading" className="font-semibold text-ink">List generation</h2>
                  <p className="mt-1 text-xs text-muted-sm">Create reusable lists with AI.</p>
                </div>
              </div>
              <ModelPicker
                name="bedrockModelId"
                label="List generation model"
                surface="generation"
                value={bedrockModelId}
                onChange={setBedrockModelId}
                keyConfigured={anthropicKeyConfigured}
              />
              <div className="mt-5 border-t border-line pt-4">
                <label htmlFor="aiDailyLimit" className={labelCls}>
                  AI daily limit (per user)
                </label>
                <input
                  id="aiDailyLimit"
                  type="number"
                  className={fieldCls}
                  min={1}
                  max={100}
                  step={1}
                  value={aiDailyLimit}
                  onChange={(e) => setAiDailyLimit(Number(e.target.value))}
                />
                <p className="mt-1 text-xs text-muted-sm">1–100 generations (default {DEFAULT_AI_DAILY_LIMIT}). Resets at midnight UTC.</p>
              </div>
            </section>

            <section aria-labelledby="chat-heading" className="min-w-0 rounded-2xl border border-line bg-surface p-5 shadow-sm">
              <div className="mb-5 flex items-start gap-3">
                <span className="rounded-lg bg-brand-50 p-2 text-brand-700"><MessageSquare aria-hidden="true" className="size-5" /></span>
                <div>
                  <h2 id="chat-heading" className="font-semibold text-ink">AI Chat</h2>
                  <p className="mt-1 text-xs text-muted-sm">An independent model and budget for the assistant.</p>
                </div>
              </div>
              <ModelPicker
                name="aiChatModelId"
                label="Chat model"
                surface="chatConversation"
                value={aiChatModelId}
                onChange={setAiChatModelId}
                keyConfigured={anthropicKeyConfigured}
              />
              <div className="mt-5 border-t border-line pt-4">
                <label htmlFor="aiChatDailyTokenLimit" className={labelCls}>
                  Chat token limit (per user per day)
                </label>
                <input
                  id="aiChatDailyTokenLimit"
                  type="number"
                  className={fieldCls}
                  min={CHAT_TOKEN_LIMIT_MIN}
                  max={CHAT_TOKEN_LIMIT_MAX}
                  step={10000}
                  value={aiChatDailyTokenLimit}
                  onChange={(e) => setAiChatDailyTokenLimit(Number(e.target.value))}
                />
                <p className="mt-1 text-xs text-muted-sm">
                  {CHAT_TOKEN_LIMIT_MIN.toLocaleString()}–{CHAT_TOKEN_LIMIT_MAX.toLocaleString()} input + output tokens (default{" "}
                  {DEFAULT_AI_CHAT_DAILY_TOKEN_LIMIT.toLocaleString()}).
                  Resets at midnight UTC.
                </p>
                <p className="mt-2 rounded-lg bg-brand-50 px-3 py-2 text-xs text-brand-700" aria-live="polite">
                  {`≈ $${dailyTokenCapUsd(aiChatModelId, aiChatDailyTokenLimit).toFixed(2)} per user per day at the selected chat model (estimate)`}
                </p>
              </div>
            </section>
          </div>

          <section aria-labelledby="storage-heading" className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
            <div className="grid gap-4 md:grid-cols-2 md:gap-6">
              <div>
                <h2 id="storage-heading" className="text-sm font-semibold text-ink">Chat privacy</h2>
                <p className="mt-1 text-xs text-muted-sm">
                  Card numbers, SSN-like numbers, and password-like values are redacted before
                  storage either way.
                </p>
              </div>
              <div>
                <label htmlFor="aiChatTranscriptMode" className={labelCls}>
                  Conversation storage
                </label>
                <select
                  id="aiChatTranscriptMode"
                  className={fieldCls}
                  value={aiChatTranscriptMode}
                  onChange={(e) => setAiChatTranscriptMode(e.target.value as AiChatTranscriptMode)}
                >
                  <option value="full">Full — keep conversation text for 30 days</option>
                  <option value="metadata-only">Metadata only — usage details, no conversation text</option>
                </select>
              </div>
            </div>
          </section>

          <div className="flex flex-col gap-4 border-t border-line pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-xl text-xs text-muted-sm">
              Estimates use list prices checked {AI_RATES_CHECKED_ON}. Bedrock usage bills to AWS;
              Anthropic API usage bills to your Anthropic Console credit. Provider invoices are
              authoritative.
            </p>
            <Button type="submit" disabled={saving} className="shrink-0">
              {saving ? "Saving…" : "Save settings"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
